import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import qs from 'query-string';
import { connect } from 'react-redux';

import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import ErrorView from '../../../../components/global/ErrorView';
import {
  AlbumForContributorShare,
  AlbumFolderForContributorShare,
} from '../../../../graphql/queries/contributors';
import { autoContributorJoin as autoContributorJoinMutation } from '../../../../graphql/mutations/contributors';
import { getErrorString } from '../../../../util/normalize-errors';
import { isAlbum } from '../../../../util/album';
import {
  getAutoContributorLinkHandlerRedirectMode,
  REDIRECT_MODE,
} from './helpers';
import { MeQuery } from '../../../../graphql/queries/account';
import { isAlbumFolderOwner, isAlbumOwner } from '../../../../util/account';
import AutoContributorLinkHandlerHeadMeta from './AutoContributorLinkHandlerHeadMeta';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAlbum: PropTypes.bool,
  loadingAlbumFolder: PropTypes.bool,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  autoContributorJoin: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.any,
  }),
  authenticated: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAccount: PropTypes.bool,
};

const AutoContributorLinkHandler = ({
  album,
  albumFolder,
  loadingAlbum,
  loadingAlbumFolder,
  history,
  autoContributorJoin,
  location,
  authenticated,
  account,
  loadingAccount,
}) => {
  const entity = album || albumFolder;
  const autoContributorSettings = entity?.auto_contributor_settings;
  const { mode: redirectModeOverride, slideshow } = qs.parse(location?.search);
  const [error, setError] = useState('');

  const redirectMode =
    redirectModeOverride ||
    getAutoContributorLinkHandlerRedirectMode(autoContributorSettings);

  const viewUrl = (() => {
    if (slideshow === '1' && entity?.slideshow) {
      return `/albums/${entity?.id}/slideshow?contribute=1`;
    }

    // Authenticated, SB users now see FWG as part of their PM
    if (authenticated && entity?.is_web_gallery) {
      return `/app/shutterbug/hierarchy-items/${entity?.id}`;
    }

    if (entity?.is_web_gallery) {
      return isAlbum(entity)
        ? `/galleries/view/album/${entity?.id}`
        : `/galleries/view/folder/${entity?.id}`;
    }

    // Authenticated, SB users now see FWG as part of their PM
    return isAlbum(entity)
      ? `/app/shutterbug/albums/${entity?.id}`
      : `/app/shutterbug/folders/${entity?.id}`;
  })();

  const uploadUrl = `/contributors/upload/${entity?.id}`;

  const autoContribute = async () => {
    if (!entity || !autoContributorSettings) {
      return;
    }

    const isOwner = isAlbum(entity)
      ? isAlbumOwner(account, entity)
      : isAlbumFolderOwner(account, entity);

    if (isOwner) {
      const redirectUrl = isAlbum(entity)
        ? `/app/shutterbug/albums/${entity?.id}`
        : `/app/shutterbug/folders/${entity?.id}`;

      history.replace(redirectUrl);
    }

    if (!authenticated && entity.is_web_gallery) {
      history.replace(viewUrl);
      return;
    }

    if (!authenticated) {
      setError('You do not have permissions to view this gallery.');
      return;
    }

    try {
      await autoContributorJoin(entity.id);

      if (redirectMode === REDIRECT_MODE.UPLOAD) {
        history.replace(uploadUrl);
      } else {
        history.replace(viewUrl);
      }
    } catch (err) {
      setError(getErrorString(err));
    }
  };

  useEffect(() => {
    if (!loadingAccount && (album || albumFolder)) {
      autoContribute();
    }
  }, [album, albumFolder, loadingAccount]);

  if (error) {
    return (
      <ErrorView
        location={{
          state: {
            error,
          },
        }}
      />
    );
  }

  if (loadingAlbum || loadingAlbumFolder || loadingAccount) {
    return <FullScreenLoader />;
  }

  if (!loadingAlbum && !loadingAlbumFolder && !entity) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'It looks like the link provided does not match any of our records.',
          },
        }}
      />
    );
  }

  if (entity && !autoContributorSettings) {
    return (
      <ErrorView
        location={{
          state: {
            error: "You don't have priviledges to perform this action.",
          },
        }}
      />
    );
  }

  return (
    <Fragment>
      <AutoContributorLinkHandlerHeadMeta entity={entity} />
      <FullScreenLoader />
    </Fragment>
  );
};

AutoContributorLinkHandler.propTypes = propTypes;

export { AutoContributorLinkHandler as AutoContributorLinkHandlerComp };

const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
  options: ({ match }) => ({
    variables: {
      id: match.params.entityId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    loadingAlbum: loading,
  }),
});

const withAlbumFolderForContributorShare = graphql(
  AlbumFolderForContributorShare,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.entityId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loadingAlbumFolder: loading,
    }),
  },
);

const withAutoContributorJoin = graphql(autoContributorJoinMutation, {
  props: ({ mutate }) => ({
    autoContributorJoin: id =>
      mutate({
        variables: {
          input: {
            id,
          },
        },
      }),
  }),
});

const withUser = graphql(MeQuery, {
  props: ({ data: { me, loading } = {} }) => ({
    account: me,
    loadingAccount: loading,
  }),
  skip: ownProps => !ownProps.authenticated,
});

const mapStateToProps = ({ auth }) => {
  return {
    authenticated: auth.authenticated,
  };
};

export default compose(
  withRouter,
  withAlbumForContributorShare,
  withAlbumFolderForContributorShare,
  withAutoContributorJoin,
  connect(mapStateToProps),
  withUser,
)(AutoContributorLinkHandler);
